import type { Day, RentalBillingParams } from '@app/sales/rental-billing/rental-billing.model';
import { addMonthsClamped, firstOfMonth, frDay } from '@app/sales/rental-billing/rental-days';

// Forme JSON de rental_terms côté serveur (common/src/dao.rs RentalTerms) ; tarifref et vat_rate sont calculés à la lecture, jamais renvoyés.
export interface RentalTier {
  uprice_taxx: number;
  starting_period: number;
  fse_lpp?: string | null;
  tarifref?: number | null;
  valid_from?: string | null;
}

export interface RentalTrigger {
  type: 'event' | 'recurring';
  event_name?: 'delivery' | 'return';
  period_type_id?: number;
  period_duration?: number;
}

export interface RentalLinked {
  product_id: number;
  quantity: number;
  billing_trigger: RentalTrigger;
}

export interface RentalCard {
  id: number;
  name: string;
  vat: number;
  vat_rate?: number | null;
  period: number;
  tiered_pricing: RentalTier[];
  is_active: boolean;
  linked_products: RentalLinked[];
  data?: Record<string, any> | null;
  error?: string | null;
  seq?: number;
  billing_error?: string;
  products_count?: number;
  products?: { id: number; name: string | null }[];
}

export interface RentalProductHead {
  id: number;
  name: string | null;
  template: number | null;
  partnbloc: string | null;
  partnbsup: string | null;
  partnbman?: string | null;
  is_sold: boolean | null;
  is_purchased?: boolean | null;
}

export interface RentalProductRow extends RentalProductHead { cards: RentalCard[]; }

export interface RentalProductSheet { product: RentalProductHead; cards: RentalCard[]; linked_names: Record<string, string | null>; }

export interface TaxRate { id: number; typ: number; name: string; rate: number; is_active: boolean; }

// Défauts du moteur pour une clé absente de data.billing (RentalBillingParams::default du serveur).
export const DEFAULT_BILLING: RentalBillingParams = {
  interruption_rule: 'PAUSE', end_rule: 'FULL_PERIOD', return_day_billed: false, overbilling: 'CREDIT_NOTE',
  start_before_delivery: true, prescription_required: false, price_source: 'CARD',
};

// Jeu Sécu : les 7 règles qu'exige fse_card_faults (convention PSDM art. 30 §2), et jour de reprise facturé (JP 28/10, non contrôlé par le serveur).
export const SECU_BILLING: RentalBillingParams = {
  interruption_rule: 'PAUSE', end_rule: 'FULL_PERIOD', return_day_billed: true, overbilling: 'COMPENSATION',
  start_before_delivery: false, prescription_required: true, price_source: 'LPP_TARIF',
};

export const BILLING_KEYS: (keyof RentalBillingParams)[] = [
  'interruption_rule', 'pause_close_after_days', 'end_rule', 'return_day_billed', 'overbilling', 'start_before_delivery', 'prescription_required', 'price_source',
];

// Périodes que le moteur sait facturer (PeriodUnit::from_c_values_16) ; heure et demi-journée bloquent la ligne. Libellés : c_values 16.
export const BILLABLE_PERIODS = [3, 4, 9, 5, 6, 7, 8];

export function billingOf(card: RentalCard): RentalBillingParams {
  return { ...DEFAULT_BILLING, ...(card.data?.['billing'] ?? {}) };
}

export function hasExplicitRules(card: RentalCard): boolean {
  return card.data?.['billing'] != null;
}

// Mêmes écarts, dans le même ordre, que fse_card_faults ; vide = le contrat de location peut servir à un dossier pris en charge par la Sécu.
export function secuFaults(b: RentalBillingParams): string[] {
  const f: string[] = [];
  if (b.interruption_rule !== 'PAUSE') f.push('interruption : Pause attendue');
  if (b.overbilling !== 'COMPENSATION') f.push('trop-facturé : report attendu');
  if (b.start_before_delivery) f.push('aucun début avant la livraison');
  if (b.pause_close_after_days != null) f.push('pas de clôture de pause');
  if (b.end_rule !== 'FULL_PERIOD') f.push('dernière période due en entier');
  if (!b.prescription_required) f.push('prescription exigée');
  if (b.price_source === 'CARD') f.push('prix au tarif LPP');
  return f;
}

// Paliers en vigueur le jour `day` : le jeu daté le plus récent déjà commencé, sinon le jeu sans date.
export function gridAt(tiers: RentalTier[], day: string): RentalTier[] {
  const dates = [...new Set(tiers.map((t) => t.valid_from?.slice(0, 10) ?? ''))].filter((d) => d && d <= day).sort();
  const at = dates.length ? dates[dates.length - 1] : '';
  return tiers.filter((t) => (t.valid_from?.slice(0, 10) ?? '') === at).sort((a, b) => a.starting_period - b.starting_period);
}

// Prix de la première période aujourd'hui : HT du contrat, ou tarif LPP (TTC) du palier 1 quand le prix vient de la nomenclature.
export function firstPrice(card: RentalCard, day: string): { value: number | null; lpp: boolean } {
  const lpp = billingOf(card).price_source !== 'CARD';
  const t = gridAt(card.tiered_pricing, day).find((x) => x.starting_period === 1);
  if (!t) return { value: null, lpp };
  return { value: lpp ? (t.tarifref ?? null) : t.uprice_taxx, lpp };
}

// Prix HT d'un palier tel que saisi : null = valable ; vide, non numérique ou à plus de 2 décimales = refusé (avant : 0 € en silence).
export function tierPriceFault(v: unknown): string | null {
  if (v === null || v === undefined || (typeof v === 'string' && !v.trim())) return 'prix vide';
  const n = typeof v === 'number' ? v : Number(String(v).trim().replace(',', '.'));
  if (!Number.isFinite(n)) return 'prix non numérique';
  return Math.round(n * 100) / 100 === n ? null : 'plus de 2 décimales';
}

// Écarts de prix de toutes les grilles, un par palier fautif ; vide = enregistrement permis.
export function priceFaults(grids: { from: Day | null; tiers: RentalTier[] }[]): string[] {
  return grids.flatMap((g) => g.tiers.flatMap((t) => {
    const f = tierPriceFault(t.uprice_taxx);
    return f ? [`Grille ${g.from ? 'du ' + frDay(g.from) : 'sans date'}, palier dès la période ${t.starting_period} : ${f}`] : [];
  }));
}

// Jour d'effet proposé à une nouvelle grille datée : le 1er du mois qui suit le jour de Paris donné.
export function nextGridDay(today: Day): Day {
  return addMonthsClamped(firstOfMonth(today), 1);
}

// Corps d'un évènement rental_terms : le contrat de location sans ce que la lecture calcule pour l'écran.
export function cardPayload(card: RentalCard): Record<string, any> {
  return {
    id: card.id, name: card.name.trim(), vat: card.vat, period: card.period, is_active: card.is_active,
    tiered_pricing: card.tiered_pricing.map((t) => {
      const o: Record<string, any> = { uprice_taxx: Number(t.uprice_taxx) || 0, starting_period: Number(t.starting_period) };
      if (t.fse_lpp && String(t.fse_lpp).trim()) o['fse_lpp'] = String(t.fse_lpp).trim();
      if (t.valid_from) o['valid_from'] = t.valid_from;
      return o;
    }),
    linked_products: card.linked_products.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), billing_trigger: l.billing_trigger })),
    data: card.data ?? null,
  };
}

// Effet de chaque réglage, en une ligne, d'après les commentaires de RentalBillingParams (common/src/dao.rs).
export const RULE_HINTS: Record<string, string> = {
  'interruption_rule.PAUSE': 'La période s\'arrête pendant l\'interruption et reprend au retour (règle des contrats Sécu).',
  'interruption_rule.FULL_PERIOD': 'Une période qui garde au moins un jour de présence est due en entier ; la grille part du premier jour de présence, recalée (avoir complet ou report) s\'il change après facture.',
  'interruption_rule.PRORATA': 'Chaque période est due au prorata de ses jours de présence ; la grille part du BL, un début différé ou avancé fait une première période au prorata.',
  'end_rule.FULL_PERIOD': 'La dernière période entamée est due en entier.',
  'end_rule.PRORATA': 'La dernière période est due au prorata de ses jours de présence.',
  'overbilling.CREDIT_NOTE': 'Avoir scellé, qui cite la facture d\'origine, dès que le trop-facturé est connu (en Pause aussi, sans attendre la reprise).',
  'overbilling.COMPENSATION': 'Aucune pièce négative : le trop payé couvre les périodes suivantes (Sécu).',
  'price_source.CARD': 'Prix HT des paliers du contrat ; sans palier, prix saisi sur la ligne du dossier.',
  'price_source.LPP_TARIF': 'Tarif de responsabilité du code LPP du palier, en vigueur le premier jour de chaque période.',
  'price_source.LPP_PLV': 'Prix limite de vente du code LPP du palier, en vigueur le premier jour de chaque période.',
  return_day_billed: 'Non : la facturation s\'arrête la veille de la reprise du matériel.',
  start_before_delivery: 'Autorise une décision de début de facturation antérieure à la date du BL.',
  prescription_required: 'Une période n\'est due que si tous ses jours sont couverts par une prescription.',
  pause_close_after_days: 'En Pause : une interruption plus longue clôt la période, due en entier (28 pour la PPC). Vide = jamais.',
};
